import { nanoid } from 'nanoid'
import { isEqual, cloneDeep } from 'lodash'
import { makeAutoObservable, reaction, type IReactionDisposer } from 'mobx'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { SegmentBox } from 'entities/Segment'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { type Segment } from 'entities/Segment/model/Segment'
import {
  EnumEnrollmentMode,
  type IApplyChangeModal,
  type IEnrollmentInitProps,
  type IEnrollmentModalConfig,
} from 'widgets/ContactEnrollment/store/types'
import { FilterEditorStore, type IFilterStoreProps } from 'widgets/FilterEditor'
import { EnrollmentItemType } from 'widgets/ContactEnrollment/types/types'
import type { ISearchDropdownStoreConfig } from 'widgets/SearchDropdown/store/types'
import { EnrollmentListStore } from './enrollmentList'
import { EnrollmentModal } from '../ui/EnrollmentModal'

type IEnrollmentStoreConfig = {
  filterConfig: IFilterStoreProps
  searchDropdownConfig?: ISearchDropdownStoreConfig
}

export const RE_ENROLL_CONTACTS_FILTER_KEYS = ['message_received_body', 'inbound_message_inbox']

export class EnrollmentStore {
  private _modalId: string = nanoid()
  private _modalApplyChangeId: string = nanoid()
  private readonly _enrollmentListStore: EnrollmentListStore
  private _mode: EnumEnrollmentMode = EnumEnrollmentMode.Edit
  private _segmentBox = new SegmentBox()
  private _disposerHasChangesSegment: IReactionDisposer | null = null
  private readonly _filterEditorStore: FilterEditorStore
  private _isContactReEnroll = false
  private _isShowContactsAlert = false

  constructor(
    { filterConfig, searchDropdownConfig }: IEnrollmentStoreConfig,
    private _entities: IEntitiesStore
  ) {
    makeAutoObservable(this)

    this._enrollmentListStore = new EnrollmentListStore(this._entities, {
      searchDropdownConfig: searchDropdownConfig,
    })
    this._filterEditorStore = new FilterEditorStore(
      {
        ...filterConfig,
        showSpawned: true,
        mutateFilterGroups: (groups, filterGroups) => filterGroups,
      },
      this._entities
    )
  }

  reactionHasChangesSegment = () => {
    this._disposerHasChangesSegment?.()
    this._disposerHasChangesSegment = reaction(
      () => this._filterEditorStore.groups.toSegmentFilters(),
      (value) => {
        if (this._segmentBox.segment) {
          const filtersParams = cloneDeep(this._segmentBox.segment.filtersParams)

          this._segmentBox.setHasChanges(!isEqual(value, filtersParams))
        } else {
          const countWithValue = !!this._filterEditorStore.groups.countWithValue
          const countWithOutValue = !!this._filterEditorStore.groups.countWithOutValue

          if (countWithOutValue) return this._segmentBox.setHasChanges(false)

          this._segmentBox.setHasChanges(countWithValue)
        }
      }
    )
  }

  initReactions = () => {
    this.reactionHasChangesSegment()
  }

  initEnrollmentList = ({ filters, number_id, confirmEnrollment }: IEnrollmentInitProps) => {
    this._enrollmentListStore.init(filters, number_id, confirmEnrollment)
  }

  get isContactReEnroll() {
    return this._isContactReEnroll
  }

  get isShowContactsAlert() {
    return this._isShowContactsAlert
  }

  setIsContactReEnroll = (value: boolean) => {
    this._isContactReEnroll = value
  }

  setIsShowContactsAlert = (value: boolean) => {
    this._isShowContactsAlert = value
  }

  isReEnrollFiltersSelected = (list: Segment[]) => {
    return list?.some((filter) => {
      return filter.filtersList.some((filterGroup) => {
        return filterGroup.filters.some((filter) =>
          RE_ENROLL_CONTACTS_FILTER_KEYS.includes(filter.key)
        )
      })
    })
  }

  setContactReEnrollAlert = (list: Segment[]) => {
    const isAlertFilters = this.isReEnrollFiltersSelected(list)

    this.setIsShowContactsAlert(isAlertFilters && this._isContactReEnroll)
  }

  openModal = ({
    title,
    titleDividerContent,
    allowEmptyCount,
    filters,
    action,
    number_id,
  }: IEnrollmentModalConfig) => {
    this.initEnrollmentList({ filters, number_id })
    this.initReactions()

    const dispose = (isAllowed: boolean) => {
      if (!isAllowed) return

      this._disposerHasChangesSegment?.()
      this._enrollmentListStore.reset()
      this._segmentBox.removeSegment()
      this._filterEditorStore.dispose()

      modalStore.removeModal(this._modalId)
    }

    const onCancel = () => {
      action.cancel().then(dispose)
    }

    const confirmAction = () => {
      action
        .confirm(
          filters,
          this._enrollmentListStore.totalCount,
          this._enrollmentListStore.totalInvalidCount
        )
        .then(dispose)
    }

    const onConfirm = () => {
      this.applyChangeModal(async () => {
        await this._enrollmentListStore.waitForCount()
        confirmAction()
      })
    }

    const onClose = () => {
      this.applyChangeModal(() => {
        dispose(true)
      }, true)
    }

    modalStore.addModal({
      id: this._modalId,
      title: title ?? 'Contact enrollment',
      titleDividerContent: titleDividerContent,
      ModalContent: EnrollmentModal,
      ModalContentProps: {
        onCancel,
        onConfirm,
        onClose,
        allowEmptyCount,
        enrollmentListStore: this._enrollmentListStore,
        filterEditorStore: this._filterEditorStore,
        segmentBox: this._segmentBox,
        applyChangeModal: this.applyChangeModal,
        mode: this.mode,
      },
      width: 1000,
      pureContent: true,
      onClose,
    })
  }

  applyChangeModal: IApplyChangeModal = (onCallback, notUseApplyCallback) => {
    const segmentHasChanges = this._segmentBox.hasChanges
    const originalSegment = this._segmentBox.segment

    if (!segmentHasChanges || !originalSegment) {
      onCallback?.()

      return
    }

    const index = this._enrollmentListStore.filters.filters.findIndex(
      (filter) => filter.id === originalSegment.id
    )
    const name = index === -1 ? this._enrollmentListStore.filters.filters.length + 1 : index + 1
    this._modalApplyChangeId = nanoid()

    modalStore.addModal({
      id: this._modalApplyChangeId,
      type: ModalTypeList.WARNING,
      title: (
        <>
          Apply changes to <br /> “Filter {name}”?
        </>
      ),
      primaryAction: {
        text: 'Apply changes',
        onAction: () => {
          const updatedSegment = this._filterEditorStore.groups.toSegment({
            id: originalSegment.id,
            organization_id: originalSegment.organizationId,
          })

          this._enrollmentListStore.addOrUpdateItem({
            type: EnrollmentItemType.Filter,
            payload: updatedSegment,
          })
          this._segmentBox.setHasChanges(false)
          this._enrollmentListStore.preview.resetItemActive(this._segmentBox)
          modalStore.closeModal(this._modalApplyChangeId)
          !notUseApplyCallback && onCallback?.()
        },
      },
      secondaryAction: {
        text: 'Keep editing',
        onAction: () => {
          modalStore.closeModal(this._modalApplyChangeId)
        },
      },
      ariaLabel: 'ApplyChange',
    })
  }

  setTitleModal = ({
    title,
    titleDividerContent,
  }: {
    title: string
    titleDividerContent: string
  }) => {
    const modal = modalStore.getModal(this._modalId)

    if (modal) {
      if ('title' in modal) {
        modal.title = title
      }
      if ('titleDividerContent' in modal) {
        modal.titleDividerContent = titleDividerContent
      }
    }
  }

  setMode = (mode: EnumEnrollmentMode) => {
    this._mode = mode
  }

  setLoading = (status: boolean) => {
    this._enrollmentListStore.setLoading(status)
  }

  get total() {
    return this._enrollmentListStore.totalCount
  }

  get skipped() {
    return this._enrollmentListStore.totalInvalidCount
  }

  get mode() {
    return this._mode
  }

  get filterEditorStore() {
    return this._filterEditorStore
  }
}
